import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { heroSlides } from '@/mocks/videos';

export default function HeroCarousel() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % heroSlides.length);
    }, 5200);
    return () => clearInterval(timer);
  }, [paused]);

  const go = (dir: number) => {
    setActive((prev) => (prev + dir + heroSlides.length) % heroSlides.length);
  };

  const slide = heroSlides[active];

  return (
    <section
      className="relative h-[480px] w-full overflow-hidden md:h-[600px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点内容轮播"
    >
      {heroSlides.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${index === active ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 焦点海报`}
            title={`${item.title} - 91视频在线`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/70 to-foreground-950/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/30" />

      <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-center px-4 md:px-8">
        <div key={slide.id} className="animate-fade-up max-w-2xl">
          <span className="font-label inline-flex items-center gap-2 rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1 text-xs font-medium text-background-100 backdrop-blur">
            <i className="ri-fire-fill text-accent-400" />
            精选推荐 · {slide.typeLabel}
          </span>
          <h1 className="font-heading mt-4 text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
            {slide.title}
          </h1>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-background-200 md:text-base">
            {slide.tagline}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/detail/${slide.id}`)}
              className="font-label inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-play-fill text-lg" />
              立即观看
            </button>
            <button
              type="button"
              onClick={() => navigate(`/category/${slide.category}`)}
              className="font-label inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 bg-background-50/10 px-5 py-3 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
            >
              <i className="ri-layout-grid-line text-lg" />
              进入分类
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {heroSlides.map((item, index) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setActive(index)}
            aria-label={`切换到 ${item.title}`}
            className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
              index === active ? 'w-7 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
            }`}
          />
        ))}
      </div>

      <div className="absolute bottom-6 right-4 z-10 hidden items-center gap-2 md:flex md:right-8">
        <button
          type="button"
          onClick={() => setPaused((prev) => !prev)}
          aria-label={paused ? '继续轮播' : '暂停轮播'}
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 bg-background-50/10 text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
        >
          <i className={paused ? 'ri-play-line' : 'ri-pause-line'} />
        </button>
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="上一张"
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 bg-background-50/10 text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="下一张"
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 bg-background-50/10 text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>
    </section>
  );
}